import React from "react";
import { motion } from "framer-motion";
import {
  Search,
  ClipboardList,
  MessageSquareText,
  Brain,
  Code2,
  FlaskConical,
  Rocket,
  LineChart,
  type LucideIcon,
} from "lucide-react";
import AnimatedSection from "./AnimatedSection";
import SectionHeader from "./SectionHeader";

type Step = {
  number: string;
  title: string;
  description: string;
  icon: LucideIcon;
};

const steps: Step[] = [
  { number: "01", title: "Discovery", description: "Understand goals, users, and business challenges.", icon: Search },
  { number: "02", title: "Requirement Analysis", description: "Define scope, channels, integrations, and KPIs.", icon: ClipboardList },
  { number: "03", title: "Conversation Design", description: "Map user flows and craft natural dialogues.", icon: MessageSquareText },
  { number: "04", title: "AI Training", description: "Train models on your data and knowledge base.", icon: Brain },
  { number: "05", title: "Development", description: "Build, integrate APIs, and connect platforms.", icon: Code2 },
  { number: "06", title: "Testing", description: "Validate accuracy, performance, and edge cases.", icon: FlaskConical },
  { number: "07", title: "Deployment", description: "Launch across channels with monitoring setup.", icon: Rocket },
  { number: "08", title: "Optimization", description: "Analyze data and continuously improve responses.", icon: LineChart },
];

const ChatBotProcess: React.FC = () => (
  <AnimatedSection as="section" className="cbot-section cbot-section--light" aria-labelledby="cbot-process-title">
    <div className="cbot-container">
      <SectionHeader
        eyebrow="Development Process"
        title="Our Chatbot Development Process"
        text="A proven 8-step framework to deliver intelligent, production-ready AI chatbots."
        light
      />

      <div className="cbot-process">
        <div className="cbot-process__line" aria-hidden="true" />
        {steps.map((step, index) => {
          const Icon = step.icon;
          return (
            <motion.div
              key={step.title}
              className="cbot-process__step"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: index * 0.06 }}
            >
              <div className="cbot-process__badge">{step.number}</div>
              <div className="cbot-process__icon">
                <Icon size={20} aria-hidden="true" />
              </div>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </motion.div>
          );
        })}
      </div>
    </div>
  </AnimatedSection>
);

export default ChatBotProcess;
